import React, { useEffect, useState } from "react";
import { type KuikaModalIconIntent } from "../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import ConfirmDialog from "./confirm-dialog";
import PromptDialog from "./prompt-dialog";

// Imperative, promise-based dialogs — so any board handler can `await confirmDialog(...)` or
// `await promptDialog(...)` instead of `window.confirm` / `window.prompt`, getting the themed modals
// without each call site wiring its own state. A single <ConfirmDialogHost/> (mounted once at each
// board route root) renders whichever dialog is pending.

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  // Tints the glyph + confirm button red, for destructive actions.
  danger?: boolean;
  // Overrides the badge's meaning. Defaults to "destructive" when danger is set, "neutral" otherwise.
  intent?: KuikaModalIconIntent;
  // Overrides the badge's glyph. Defaults to a glyph that fits the resolved intent.
  iconName?: string;
}

export interface PromptOptions {
  title: string;
  message?: string;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  // Allow submitting an empty value (e.g. an optional date that clears the field).
  allowEmpty?: boolean;
  // Overrides the badge's meaning. Defaults to "create".
  intent?: KuikaModalIconIntent;
  // Overrides the badge's glyph. Defaults to "plus".
  iconName?: string;
}

interface PendingConfirm extends ConfirmOptions {
  kind: "confirm";
  resolve: (confirmed: boolean) => void;
}

interface PendingPrompt extends PromptOptions {
  kind: "prompt";
  resolve: (value: string | null) => void;
}

type Pending = PendingConfirm | PendingPrompt;

let activeListener: ((pending: Pending) => void) | null = null;

export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  return new Promise<boolean>((resolve: (confirmed: boolean) => void) => {
    if (!activeListener) {
      // No host mounted (outside the board) — fail closed rather than silently proceeding.
      resolve(false);
      return;
    }
    activeListener({ ...options, kind: "confirm", resolve });
  });
}

export function promptDialog(options: PromptOptions): Promise<string | null> {
  return new Promise<string | null>((resolve: (value: string | null) => void) => {
    if (!activeListener) {
      resolve(null);
      return;
    }
    activeListener({ ...options, kind: "prompt", resolve });
  });
}

// Resolve a still-open request as cancelled (used when a new one supersedes it).
function cancelPending(pending: Pending): void {
  if (pending.kind === "confirm") pending.resolve(false);
  else pending.resolve(null);
}

// Mount once per board route. Subsequent confirmDialog()/promptDialog() calls render through it.
export const ConfirmDialogHost: React.FC = () => {
  const [pending, setPending] = useState<Pending | null>(null);

  useEffect(() => {
    activeListener = (next: Pending): void => {
      setPending((prev: Pending | null) => {
        if (prev) cancelPending(prev);
        return next;
      });
    };
    return () => {
      activeListener = null;
    };
  }, []);

  const settleConfirm = (confirmed: boolean): void => {
    setPending((prev: Pending | null) => {
      if (prev?.kind === "confirm") prev.resolve(confirmed);
      return null;
    });
  };

  const settlePrompt = (value: string | null): void => {
    setPending((prev: Pending | null) => {
      if (prev?.kind === "prompt") prev.resolve(value);
      return null;
    });
  };

  const confirm: PendingConfirm | null = pending?.kind === "confirm" ? pending : null;
  const prompt: PendingPrompt | null = pending?.kind === "prompt" ? pending : null;

  return (
    <>
      <ConfirmDialog
        open={confirm !== null}
        title={confirm?.title ?? ""}
        message={confirm?.message ?? ""}
        confirmLabel={confirm?.confirmLabel}
        cancelLabel={confirm?.cancelLabel}
        danger={confirm?.danger ?? false}
        intent={confirm?.intent}
        iconName={confirm?.iconName}
        onConfirm={() => settleConfirm(true)}
        onCancel={() => settleConfirm(false)}
      />
      <PromptDialog
        open={prompt !== null}
        title={prompt?.title ?? ""}
        message={prompt?.message}
        label={prompt?.label}
        placeholder={prompt?.placeholder}
        defaultValue={prompt?.defaultValue}
        confirmLabel={prompt?.confirmLabel}
        cancelLabel={prompt?.cancelLabel}
        allowEmpty={prompt?.allowEmpty}
        intent={prompt?.intent}
        iconName={prompt?.iconName}
        onSubmit={(value: string) => settlePrompt(value)}
        onCancel={() => settlePrompt(null)}
      />
    </>
  );
};
